import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ScanLine, Printer, Layers, BarChart3, ExternalLink } from 'lucide-react';
import { useAdminContext } from './adminContext';
import { CheckInScanner } from '../../components/CheckInScanner';
import { AdminHelpdesk } from '../../components/AdminHelpdesk';
import { AdminBadgeProduction } from '../../components/AdminBadgeProduction';
import { AdminReportsAnalytics } from '../../components/AdminReportsAnalytics';

export function AdminOperationsPage() {
  const { adminKey } = useAdminContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const initialTab =
    rawTab === 'helpdesk'
      ? 'helpdesk'
      : rawTab === 'production'
      ? 'production'
      : rawTab === 'reports'
      ? 'reports'
      : 'scanner';
  const [operationsTab, setOperationsTab] = useState<'scanner' | 'helpdesk' | 'production' | 'reports'>(initialTab);

  const handleTabChange = (tab: 'scanner' | 'helpdesk' | 'production' | 'reports') => {
    setOperationsTab(tab);
    setSearchParams(tab === 'scanner' ? {} : { tab });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Sub-tab Navigation */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          type="button"
          onClick={() => handleTabChange('scanner')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            operationsTab === 'scanner'
              ? 'bg-[#0A3D27] text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <ScanLine className="w-4 h-4" />
          <span>Visitor Check-in / Gate Scanner</span>
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('helpdesk')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            operationsTab === 'helpdesk'
              ? 'bg-[#0A3D27] text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Printer className="w-4 h-4" />
          <span>Onsite Helpdesk & Badge Re-print</span>
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('production')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            operationsTab === 'production'
              ? 'bg-[#0A3D27] text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Layers className="w-4 h-4" />
          <span>Badge Production &amp; NFC</span>
        </button>

        <button
          type="button"
          onClick={() => handleTabChange('reports')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            operationsTab === 'reports'
              ? 'bg-[#0A3D27] text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          <span>Turnout &amp; Analytics</span>
        </button>

        <a
          href="/counter/perks"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-[#0A3D27] bg-[#0A3D27]/10 hover:bg-[#0A3D27]/20 transition-all cursor-pointer ml-auto"
        >
          <span>Perks Counter</span>
          <ExternalLink className="w-3 h-3 text-[#0A3D27]" />
        </a>
      </div>

      {/* SUB-TAB A: GATE SCANNER */}
      {operationsTab === 'scanner' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200/90 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3 shadow-xs">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#0A3D27]/10 text-[#0A3D27] flex items-center justify-center">
                <ScanLine className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-xs font-bold text-slate-900">Pavilion 3 Turnstile Ingress Scanner</h4>
                <p className="text-[11px] text-slate-500">
                  Real-time pass verification with duplicate scan detection & audio feedback.
                </p>
              </div>
            </div>

            <a
              href="/checkin"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <ExternalLink className="w-3.5 h-3.5 text-[#0A3D27]" />
              <span>Open Fullscreen Kiosk Mode</span>
            </a>
          </div>

          {/* Embedded CheckInScanner */}
          <CheckInScanner adminKey={adminKey} />
        </div>
      )}

      {/* SUB-TAB B: ONSITE HELPDESK & RE-PRINT */}
      {operationsTab === 'helpdesk' && (
        <div className="space-y-6">
          <AdminHelpdesk adminKey={adminKey} />
        </div>
      )}

      {/* SUB-TAB C: BADGE PRODUCTION & NFC */}
      {operationsTab === 'production' && (
        <div className="space-y-6">
          <AdminBadgeProduction adminKey={adminKey} />
        </div>
      )}

      {/* SUB-TAB D: TURNOUT & INTERACTION ANALYTICS */}
      {operationsTab === 'reports' && (
        <div className="space-y-6">
          <AdminReportsAnalytics adminKey={adminKey} />
        </div>
      )}
    </div>
  );
}

export default AdminOperationsPage;
